import React, { useEffect } from 'react';
import { Box, Grid, Typography } from '@mui/material';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import CardView from '../../component/card/card-view';
import ButtonComponent from '../../component/button/button.component';
import TableComponent, { Data, HeadCell } from '../../component/table/table.component';
import { ConditionType, SearchKeyInput, SearchKeyInputItems } from '../../component/form/search-key-input.component';
import { MobxObjectsManager } from '../mobx-objs/mobx-manager';
import { TopBarState } from '../../component/top-bar/top-bar-state';
import i18n from '../../i18n';
import { dateFormat } from '../../constants/utils';
import type { AnagraficaOcrSessionListItem } from '../../component/archive-form/anagrafica-ocr';
import { AnagraficaOcrSessionsState } from './anagrafica-ocr-sessions-state';
import AnagraficaOcrAuditDialog from './anagrafica-ocr-audit-dialog.component';
import './scss/anagrafica-ocr-sessions.style.scss';

/**
 * 将表格 Data 断言为 OCR 会话行。
 * @param row TableComponent 行
 */
function asSessionRow(row: Data): AnagraficaOcrSessionListItem {
    return row as AnagraficaOcrSessionListItem;
}

/**
 * 格式化创建人展示名。
 * @param row 会话行
 */
function formatCreatorName(row: AnagraficaOcrSessionListItem): string {
    const user = row.created_by_user;
    if (user) {
        const name = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();
        if (name) {
            return name;
        }
    }
    return row.created_by || '—';
}

/**
 * 超管端登记文件 OCR 会话列表。
 */
const AnagraficaOcrSessionsPage = observer(() => {
    useTranslation();
    const pageState = useLocalObservable(() => new AnagraficaOcrSessionsState());
    const {
        list,
        isTableLoading,
        dataTotal,
        page,
        conditions,
        detailOpen,
        detailSession,
    } = pageState;

    useEffect(() => {
        pageState.init();
        const topbarState = MobxObjectsManager.get(TopBarState);
        topbarState.updateAnagraficaOcrSessionsTitle();
        return () => {
            MobxObjectsManager.remove(AnagraficaOcrSessionsState);
        };
    }, [pageState]);

    const filterList: SearchKeyInputItems[] = [
        {
            value: 'nickname',
            label: i18n.t('anagrafica_ocr_col_nickname'),
            type: 'TEXT',
        },
        {
            value: 'string_id',
            label: i18n.t('anagrafica_ocr_col_string_id'),
            type: 'TEXT',
        },
    ];

    const tableHeadCell: HeadCell[] = [
        {
            id: 'nickname',
            label: i18n.t('anagrafica_ocr_col_nickname'),
            renderFunction: (row) => (
                <Typography variant="body2">{asSessionRow(row).nickname || '—'}</Typography>
            ),
        },
        {
            id: 'string_id',
            label: i18n.t('anagrafica_ocr_col_string_id'),
            renderFunction: (row) => (
                <Typography variant="body2">{asSessionRow(row).string_id || '—'}</Typography>
            ),
        },
        {
            id: 'file_name',
            label: i18n.t('anagrafica_ocr_col_file_name'),
            renderFunction: (row) => (
                <Typography variant="body2">{asSessionRow(row).file_name || '—'}</Typography>
            ),
        },
        {
            id: 'country',
            label: i18n.t('anagrafica_ocr_col_country'),
            renderFunction: (row) => (
                <Typography variant="body2">{asSessionRow(row).country || '—'}</Typography>
            ),
        },
        {
            id: 'field_count',
            label: i18n.t('anagrafica_ocr_col_field_count'),
            renderFunction: (row) => {
                const item = asSessionRow(row);
                return (
                    <Typography variant="body2">
                        {item.field_count != null ? String(item.field_count) : '—'}
                    </Typography>
                );
            },
        },
        {
            id: 'created_by',
            label: i18n.t('anagrafica_ocr_col_creator'),
            renderFunction: (row) => (
                <Typography variant="body2">{formatCreatorName(asSessionRow(row))}</Typography>
            ),
        },
        {
            id: 'updated_at',
            label: i18n.t('anagrafica_ocr_col_updated_at'),
            renderFunction: (row) => {
                const item = asSessionRow(row);
                return (
                    <Typography variant="body2">
                        {item.updated_at
                            ? dateFormat(item.updated_at, 'YYYY/MM/DD HH:mm:ss')
                            : (item.created_at
                                ? dateFormat(item.created_at, 'YYYY/MM/DD HH:mm:ss')
                                : '—')}
                    </Typography>
                );
            },
        },
        {
            id: 'action',
            label: '',
            renderFunction: (row) => (
                <ButtonComponent
                    action="VIEW"
                    handleClick={() => pageState.openDetail(asSessionRow(row))}
                />
            ),
        },
    ];

    /**
     * 应用筛选条件。
     * @param next 新条件
     */
    const handleSearch = (next: ConditionType[]) => {
        void pageState.applySearch(next);
    };

    /**
     * 分页回调。
     * @param params 页码与每页条数
     */
    const changePageCallback = (params: { page: number; curRowsPerPage: number }) => {
        void pageState.changePage(params);
    };

    const renderContent = (): JSX.Element => (
        <Box className="contain">
            <Grid container justifyContent="space-between" sx={{ mb: 3 }}>
                <Grid item xs={12} md={8}>
                    <SearchKeyInput
                        items={filterList}
                        conditions={conditions}
                        onSearch={handleSearch}
                        showConditionsBox
                    />
                </Grid>
            </Grid>
            <TableComponent
                showChecked={0}
                list={list}
                isUseCustomPagination
                headCellList={tableHeadCell}
                isTableLoading={isTableLoading}
                isPagination
                allCount={dataTotal}
                changePageCallback={changePageCallback}
                isCurrentPageData
                currentPage={page - 1}
                rowsPerPage={pageState.pageSize}
            />
            <AnagraficaOcrAuditDialog
                open={detailOpen}
                onClose={() => pageState.closeDetail()}
                title={i18n.t('anagrafica_ocr_session_detail_title')}
                sessionFields={detailSession?.fields}
                meta={detailSession ? {
                    same_addr: detailSession.same_addr,
                    confirmed_by_name: formatCreatorName(detailSession),
                    confirmed_by: detailSession.created_by,
                    created_by_user: detailSession.created_by_user,
                } : null}
            />
        </Box>
    );

    return (
        <CardView
            className="anagrafica-ocr-sessions-page"
            contentRender={renderContent()}
        />
    );
});

export default AnagraficaOcrSessionsPage;
